import React, { useEffect, useState } from "react";
import { Row, Col, Card, Typography, Spin, Skeleton } from "antd";
import { IconCategory } from "@tabler/icons-react";
import CategoryGroupSkeleton from "./skeletons/CategoryGroupSkeleton";
import ProductCardSkeleton from "./skeletons/ProductCardSkeleton";
import { useNavigate } from "react-router-dom";
import ProductServ from "../service/ProductServ";
import SafeImage from "./SafeImage";
import GlobalTooltip from "./GlobalTooltip";
import ProductCard from "./ProductCard";

import { LocationContext } from "../LocationContext";
import { useDeliveryEstimateContext } from "../context/DeliveryEstimateContext";
import { usePreference } from "../context/PreferenceContext";
import { renderSafeText } from "../util/unitFormatter";
import "../css/DealsSection.css";

const { Text } = Typography;

const CategoryProductListing = ({ filters = {} }) => {
    const { t } = usePreference();
    const [categoryGroups, setCategoryGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { latitude, longitude, radius } = React.useContext(LocationContext);
    const deliveryContext = useDeliveryEstimateContext();
    const registerShop = deliveryContext?.registerShop;
    const formatETA = deliveryContext?.formatETA;

    // Register shops from products only if location is available
    useEffect(() => {
        if (!latitude || !longitude) return;
        categoryGroups.forEach(group => {
            if (group.products) {
                group.products.forEach(product => {
                    const shopId = product.shopId?._id || product.shopId;
                    if (shopId && registerShop) registerShop(shopId);
                });
            }
        });
    }, [categoryGroups, registerShop, latitude, longitude]);

    useEffect(() => {
        let isCurrent = true;
        fetchCategoryData(isCurrent);
        return () => { isCurrent = false; };
    }, [JSON.stringify(filters), latitude, longitude, radius]);

    const fetchCategoryData = async (isCurrent) => {
        try {
            setLoading(true);
            const params = {};
            if (filters.search) params.search = filters.search;
            if (latitude && longitude) {
                params.lat = latitude;
                params.lng = longitude;
                params.radius = radius;
            }

            // Add Category, Brand and Rating Filters
            if (filters.categories && filters.categories.length > 0) {
                params.categories = filters.categories.join(',');
            }
            if (filters.brands && filters.brands.length > 0) {
                params.brands = filters.brands.join(',');
            }
            if (filters.rating) {
                params.rating = filters.rating;
            }

            const res = await ProductServ.getHomeCategoryProducts(params);
            if (isCurrent) {
                setCategoryGroups(res?.data || res || []);
            }
        } catch (err) {
            console.error("Error fetching category products:", err);
        } finally {
            if (isCurrent) {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="category-product-listing pcu-padding-bottom-40">
                {[...Array(3)].map((_, idx) => (
                    <div key={idx} className="category-section pcu-margin-bottom-30">
                        <div className="pcu-margin-bottom-15">
                            <Skeleton.Input active size="small" style={{ width: 150, height: 24, borderRadius: 4 }} />
                        </div>
                        <div className="category-products-row pcu-flex pcu-gap-12 pcu-overflow-x-auto pcu-padding-bottom-10 pcu-scroll-hide">
                            {[...Array(10)].map((_, sidx) => (
                                <div key={sidx} className="pcu-min-w-160 pcu-max-w-160 pcu-flex-shrink-0">
                                    <ProductCardSkeleton />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (categoryGroups.length === 0) return null;

    return (
        <div className="category-product-listing">
            {categoryGroups.map((group, idx) => (
                <div key={idx} className="category-section pcu-margin-bottom-24">
                    <div className="category-header-gradient" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div
                            className="deals-header-content"
                            onClick={() => {
                                const q = filters.search ? `?search=${encodeURIComponent(filters.search)}` : '';
                                navigate(`/category/${group.category}${q}`);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <Typography.Title level={2} className="category-header-title text-header" style={{ margin: 0 }}>
                                {renderSafeText(group.category)}
                            </Typography.Title>
                        </div>
                        <span
                            onClick={() => {
                                const q = filters.search ? `?search=${encodeURIComponent(filters.search)}` : '';
                                navigate(`/category/${group.category}${q}`);
                            }}
                            className="category-see-all-btn"
                        >
                            {t('see_all', 'Show All')} ›
                        </span>
                    </div>

                    <div className="category-products-row pcu-flex pcu-gap-12 pcu-overflow-x-auto pcu-padding-bottom-20 pcu-padding-x-4 pcu-scroll-hide pcu-scrolling-touch">
                        <style>
                            {`
                                .category-products-row::-webkit-scrollbar { 
                                    display: none; 
                                }
                            `}
                        </style>

                        {(() => {
                            // Deduplicate products by name for extra safety (merges identical products with different IDs)
                            const uniqueProducts = Array.from(new Map(
                                (group.products || [])
                                    .filter(p => p.isActive !== false)
                                    .map(p => [renderSafeText(p.name).toLowerCase(), p])
                            ).values());

                            return uniqueProducts.slice(0, 10).map((product) => (
                                <div key={product._id} className="pcu-min-w-160 pcu-max-w-160 pcu-flex-shrink-0">
                                    <ProductCard product={product} formatETA={formatETA} />
                                </div>
                            ));
                        })()}
                    </div>


                </div>
            ))}
        </div>
    );
};

export default CategoryProductListing;
